/**
 * What a selection of items (the items table's, the triage area's) can have done to it at once: archive or bring back,
 * delete, set the entity, request a review, attach to another item. Each is one change on the server (one history
 * entry, one revert), and what couldn't be done (items gone, or hidden from you; a reviewer who can't see some) is
 * said after. With every item the list matches selected, they send the list's query instead of ids: the server makes
 * the change in the background (a job the list follows, still one history entry), and deleting that many asks for
 * their number to be typed.
 */
import type { ComponentChildren } from "preact";
import { useState } from "preact/hooks";
import { api } from "../../api/client.ts";
import type {
  BulkDeleted,
  BulkRequested,
  BulkResult,
  FolderRow,
  ItemFilter,
  ItemRow,
  Job,
  Member,
  ObjEntity,
  SpaceOut,
} from "../../api/types.gen.ts";
import { t } from "../../i18n/index.ts";
import { isFolderId } from "../../lib/folders.ts";
import { spaceApi } from "../../lib/paths.ts";
import { useAction } from "../../lib/resource.ts";
import { type Selection, selectedCount } from "../../lib/selection.ts";
import { toast } from "../../lib/toast.ts";
import { Button } from "../../ui/Button.tsx";
import { ConfirmDialog, Dialog } from "../../ui/Dialog.tsx";
import { EntityGlyph } from "../../ui/EntityGlyph.tsx";
import {
  AddLinkIcon,
  ArchivedIcon,
  ChooserIcon,
  FolderIcon,
  NormalizeIcon,
  RenameIcon,
  RequestReviewIcon,
  TrashIcon,
  UnarchiveIcon,
} from "../../ui/icons.tsx";
import { Menu } from "../../ui/Menu.tsx";
import { buttonClass } from "../../ui/styles.ts";
import type { ToolbarItem } from "../../ui/Toolbar.tsx";
import { MoveDialog } from "../folders/move.tsx";
import { RequestForm } from "../item/requests.tsx";
import { typeCount } from "../trash/typeCount.ts";
import { AttachForm } from "./attach.tsx";
import { NormalizeDialog } from "./normalize.tsx";
import { RenameForm } from "./rename.tsx";

type BulkAction = "archive" | "delete" | "entity" | "request" | "file" | "normalize" | "rename" | "move";

interface Props {
  space: SpaceOut;
  sel: Selection;
  /** Which actions, in order. */
  actions: readonly BulkAction[];
  entities: readonly ObjEntity[];
  members?: readonly Member[] | undefined;
  /** The list shows archived items: archiving brings them back, and they're only matched when asked for. */
  archived?: boolean;
  /** The list's query, for when every item it matches is selected (`sel.all`), and how many it matches. */
  query?: { filter: ItemFilter; total: number } | undefined;
  /** After an action: the list reloads. */
  onDone: () => void;
  /** The bar they're in (the selection's), given them as its items (their dialogs are beside it). */
  bar: (items: readonly ToolbarItem[]) => ComponentChildren;
  /** An action on every match started a job in the background: the list follows it. */
  onJob: (job: Job) => void;
  /** The list's rows on screen (who sees each: whether moving them offers matching a folder's sharing). */
  rows?: readonly ItemRow[] | undefined;
  /** The rows are in a triage area: moving them into a folder files them there. */
  triage?: boolean;
  /** The list's folders (its first rows): those selected are moved, archived or deleted with what's in them. */
  folders?: readonly FolderRow[] | undefined;
}

/** A folder picked: its version when the list has it (a change checks it), its name. */
interface Picked {
  id: string;
  ver?: string | undefined;
  name: string;
}

/** Only what folders can have done too, while some are selected. */
const FOR_FOLDERS: readonly BulkAction[] = ["archive", "move", "delete"];

/** Says how many weren't changed, if any. */
function warn(n: number, key: "bulk.skipped" | "bulk.notDeleted") {
  if (n > 0) {
    toast(t(key, { count: n }), "error");
  }
}

/** The selection's actions, as items of its bar (at the end of the list's toolbar). */
export function BulkActions(p: Props) {
  const { space, sel } = p;
  const [dialog, setDialog] = useState<"delete" | "request" | "file" | "normalize" | "rename" | "move" | null>(null);
  const done = () => {
    sel.clear();
    p.onDone();
  };
  // The folders picked (the list's own, or picked open in one: their version not known), done one by one; the items:
  // every match (the list's query), or the ids picked.
  const folders: Picked[] = sel.ids
    .filter(isFolderId)
    .map((id) => p.folders?.find((f) => f.id === id) ?? { id, name: "" });
  const ids = sel.ids.filter((id) => !isFolderId(id));
  const query = sel.all ? p.query : undefined;
  const target = query ? { filter: query.filter } : { ids };
  const items = query !== undefined || ids.length > 0;
  /** Each folder picked: `run` on it, a job followed when it's that big. */
  const eachFolder = async (run: (f: Picked) => Promise<unknown>) => {
    for (const f of folders) {
      const r = await run(f);
      if (r && typeof r === "object" && "action" in r) {
        p.onJob(r as Job);
      }
    }
  };
  /** Every match runs in the background: the list follows the job (and says how it went when it's done). */
  const started = (job: Job) => {
    p.onJob(job);
    sel.clear();
  };
  const [update, updating] = useAction(async (set: Record<string, unknown>, ok: (n: number) => string) => {
    const path = spaceApi(space, "/items/bulk");
    if (folders.length > 0) {
      // Archiving (or bringing back) a folder does what's in it too.
      await eachFolder((f) =>
        api.patch<unknown>(spaceApi(space, `/folders/${f.id}`), {
          ...set,
          apply_to_contents: true,
          ...(f.ver ? { ver: f.ver } : {}),
        }),
      );
      if (!items) {
        toast(t(set["archived"] ? "folders.archivedSelection" : "folders.unarchivedSelection"));
        done();
        return;
      }
    }
    if (query) {
      started(await api.post<Job>(path, { filter: query.filter, set }));
      return;
    }
    const r = await api.post<BulkResult>(path, {
      ...target,
      set,
      ...(p.archived ? { filter: { archived: true } } : {}),
    });
    if (r.matched > 0) {
      toast(ok(r.matched));
    }
    warn(r.skipped.length, "bulk.skipped");
    done();
  });
  const [remove, removing] = useAction(async () => {
    const path = spaceApi(space, "/items/bulk/delete");
    if (folders.length > 0) {
      await eachFolder((f) => api.del<unknown>(spaceApi(space, `/folders/${f.id}`), { query: { recursive: true } }));
      if (!items) {
        toast(t("folders.deletedSelection"));
        done();
        return;
      }
    }
    if (query) {
      started(await api.post<Job>(path, target));
      return;
    }
    const r = await api.post<BulkDeleted>(path, target);
    if (r.deleted.length > 0) {
      toast(t("bulk.deleted", { count: r.deleted.length }));
    }
    warn(r.not_found.length, "bulk.notDeleted");
    done();
  });
  const busy = updating || removing;
  const n = selectedCount(sel, query === undefined ? undefined : query.total + folders.length);
  /** Not while one's running (as the buttons are disabled). */
  const unlessBusy = (f: () => void) => () => {
    if (!busy) {
      f();
    }
  };
  // Archiving and deleting wear the icons of where things go (the Archived filter, the trash): to look for them there.
  const archive = p.archived
    ? {
        label: t("item.unarchive"),
        icon: <UnarchiveIcon size={14} />,
        run: () => update({ archived: false }, (c) => t("bulk.unarchived", { count: c })),
      }
    : {
        label: t("item.archive"),
        icon: <ArchivedIcon size={14} />,
        run: () => update({ archived: true }, (c) => t("bulk.archived", { count: c })),
      };
  const types = [
    ...p.entities.map((e) => ({
      key: e.id,
      label: e.name,
      icon: <EntityGlyph icon={e.icon} empty="dot" />,
      onSelect: () => update({ entity: e.id }, (c) => t("bulk.typed", { count: c, entity: e.name })),
    })),
    {
      key: "none",
      label: t("items.noEntity"),
      icon: <EntityGlyph icon={null} empty="none" />,
      onSelect: () => update({ entity: null }, (c) => t("bulk.untyped", { count: c })),
    },
  ];
  // On a narrow bar, they go into its "⋯" (the entities under their heading): the least used first, deleting last.
  const actions: Record<BulkAction, ToolbarItem> = {
    archive: {
      key: "archive",
      node: (
        <Button size="sm" busy={updating} onClick={archive.run}>
          {updating ? null : archive.icon}
          {archive.label}
        </Button>
      ),
      drop: 30,
      menu: [{ label: archive.label, icon: archive.icon, onSelect: unlessBusy(archive.run) }],
    },
    entity: {
      key: "entity",
      node: (
        <Menu
          label={
            <>
              <span>{t("triage.setType")}</span>
              <ChooserIcon />
            </>
          }
          class={`${buttonClass("secondary", "sm")} ${busy ? "opacity-50" : ""}`}
          items={types}
        />
      ),
      drop: 40,
      menu: types.map((e) => ({ ...e, group: t("triage.setType"), onSelect: unlessBusy(e.onSelect) })),
    },
    request: {
      key: "request",
      node: (
        <Button size="sm" disabled={busy} onClick={() => setDialog("request")}>
          <RequestReviewIcon size={14} />
          {t("requests.button")}
        </Button>
      ),
      drop: 50,
      menu: [
        {
          label: t("requests.button"),
          icon: <RequestReviewIcon size={14} />,
          onSelect: unlessBusy(() => setDialog("request")),
        },
      ],
    },
    file: {
      key: "file",
      node: (
        <Button size="sm" disabled={busy} onClick={() => setDialog("file")}>
          <AddLinkIcon size={14} />
          {t("triage.fileInto")}
        </Button>
      ),
      drop: 45,
      menu: [
        { label: t("triage.fileInto"), icon: <AddLinkIcon size={14} />, onSelect: unlessBusy(() => setDialog("file")) },
      ],
    },
    move: {
      key: "move",
      node: (
        <Button size="sm" disabled={busy} onClick={() => setDialog("move")}>
          <FolderIcon size={14} />
          {t("folders.moveTo")}
        </Button>
      ),
      drop: 42,
      menu: [
        { label: t("folders.moveTo"), icon: <FolderIcon size={14} />, onSelect: unlessBusy(() => setDialog("move")) },
      ],
    },
    rename: {
      key: "rename",
      node: (
        <Button size="sm" disabled={busy} onClick={() => setDialog("rename")}>
          <RenameIcon size={14} />
          {t("item.rename")}
        </Button>
      ),
      drop: 35,
      menu: [
        { label: t("item.rename"), icon: <RenameIcon size={14} />, onSelect: unlessBusy(() => setDialog("rename")) },
      ],
    },
    normalize: {
      key: "normalize",
      node: (
        <Button size="sm" disabled={busy} title={t("normalize.what")} onClick={() => setDialog("normalize")}>
          <NormalizeIcon size={14} />
          {t("normalize.button")}
        </Button>
      ),
      drop: 10,
      menu: [
        {
          label: t("normalize.button"),
          icon: <NormalizeIcon size={14} />,
          onSelect: unlessBusy(() => setDialog("normalize")),
        },
      ],
    },
    delete: {
      key: "delete",
      node: (
        <Button size="sm" variant="danger" busy={removing} onClick={() => setDialog("delete")}>
          {removing ? null : <TrashIcon size={14} />}
          {t("common.delete")}
        </Button>
      ),
      drop: 20,
      menu: [
        {
          label: t("common.delete"),
          icon: <TrashIcon size={14} />,
          danger: true,
          onSelect: unlessBusy(() => setDialog("delete")),
        },
      ],
    },
  };
  const close = () => setDialog(null);
  return (
    <>
      {/* Filing and renaming take the ids themselves: not for every match. */}
      {p.bar(
        p.actions
          .filter((a) => !(query && (a === "file" || a === "rename")))
          .filter((a) => folders.length === 0 || FOR_FOLDERS.includes(a))
          .map((a) => actions[a]),
      )}
      <ConfirmDialog
        open={dialog === "delete"}
        onClose={close}
        title={folders.length > 0 ? t("folders.deleteSelection") : t("bulk.deleteTitle", { count: n })}
        message={folders.length > 0 ? t("folders.deleteFolders", { count: folders.length }) : t("bulk.deleteMessage")}
        confirmLabel={t("common.delete")}
        {...(query ? typeCount(n) : {})}
        onConfirm={remove}
      />
      <Dialog open={dialog === "request"} onClose={close} title={t("bulk.requestTitle", { count: n })}>
        <RequestForm
          members={p.members}
          onCancel={close}
          send={async (reviewer, note) => {
            const path = spaceApi(space, "/items/bulk/requests");
            if (query) {
              started(await api.post<Job>(path, { ...target, reviewer, note }));
              close();
              return;
            }
            const r = await api.post<BulkRequested>(path, { ...target, reviewer, note });
            const name = p.members?.find((m) => m.id === reviewer)?.name ?? reviewer;
            if (r.requested.length > 0) {
              toast(t("bulk.requested", { count: r.requested.length, name }));
            }
            const cantSee = r.not_requested.filter((x) => x.reason === "reviewer_cant_see").length;
            if (cantSee > 0) {
              toast(t("bulk.reviewerCantSee", { count: cantSee, name }), "error");
            }
            warn(r.not_requested.length - cantSee, "bulk.skipped");
            close();
            done();
          }}
        />
      </Dialog>
      <NormalizeDialog
        space={space}
        open={dialog === "normalize"}
        onClose={close}
        title={t("normalize.title")}
        target={query ? { filter: query.filter } : { ids: sel.ids }}
        onDone={(r) => {
          if ("action" in r) {
            started(r);
            return;
          }
          if (r.items > 0) {
            toast(t("jobs.done.normalize", { count: r.items }));
          }
          warn(r.skipped.length, "bulk.skipped");
          done();
        }}
      />
      <Dialog open={dialog === "rename"} onClose={close} title={t("bulk.renameTitle", { count: n })}>
        {dialog === "rename" ? (
          <RenameForm
            space={space}
            ids={sel.ids}
            archived={p.archived ?? false}
            onCancel={close}
            onDone={(r) => {
              if (r.renamed.length > 0) {
                toast(t("bulk.renamed", { count: r.renamed.length }));
              }
              warn(r.not_found.length, "bulk.skipped");
              close();
              done();
            }}
          />
        ) : null}
      </Dialog>
      <MoveDialog
        space={space}
        open={dialog === "move"}
        onClose={close}
        title={folders.length > 0 ? t("folders.moveSelection") : t("folders.moveMany", { count: n })}
        moving={{
          kind: "items",
          ids,
          folders,
          filter: query?.filter,
          // Who sees each, when all of them are on screen.
          items: ids.every((id) => p.rows?.some((r) => r.id === id)) ? p.rows?.filter((r) => sel.has(r.id)) : undefined,
          triage: p.triage ?? false,
        }}
        onDone={(m) => ("job" in m ? started(m.job) : done())}
      />
      <Dialog open={dialog === "file"} onClose={close} title={t("bulk.fileTitle", { count: n })}>
        <AttachForm
          space={space}
          ids={sel.ids}
          entities={p.entities}
          onClose={close}
          onDone={(to) => {
            toast(t("bulk.filed", { count: sel.ids.length, target: to.label }));
            done();
          }}
        />
      </Dialog>
    </>
  );
}
